import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getWatermark,
  createWatermark,
  updateWatermark,
  deleteWatermark,
  uploadWatermarkImage,
} from "../../services/watermarkService";
import { useAuth } from "../../context/AuthContext";

// ─── Position Definitions ───────────────────────────────────────────────────

const POSITIONS = [
  { value: "TopLeft",     label: "↖",  title: "Top Left" },
  { value: "TopRight",    label: "↗",  title: "Top Right" },
  { value: "Center",      label: "⊙",  title: "Center" },
  { value: "BottomLeft",  label: "↙",  title: "Bottom Left" },
  { value: "BottomRight", label: "↘",  title: "Bottom Right" },
];

// 3×3 grid — null = spacer cell
const GRID_MAP = [
  ["TopLeft",    null,     "TopRight"],
  [null,         "Center", null],
  ["BottomLeft", null,     "BottomRight"],
];

// ─── Position Style Helper ───────────────────────────────────────────────────

const calcPositionStyle = (pos, containerW, containerH) => {
  const padX = containerW * 0.03;
  const padY = containerH * 0.03;
  switch (pos) {
    case "TopLeft":     return { top: padY, left: padX };
    case "TopRight":    return { top: padY, right: padX };
    case "BottomLeft":  return { bottom: padY, left: padX };
    case "BottomRight": return { bottom: padY, right: padX };
    default:            return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }
};

// ─── Component ───────────────────────────────────────────────────────────────

const WatermarkSettingsModal = ({ onClose, onSaved }) => {
  const { user } = useAuth();
  const countryCode = user?.CountryCode ?? user?.countryCode ?? 49;
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [existingWm, setExistingWm]   = useState(null);

  // Image state
  const [imageFile, setImageFile]         = useState(null);   // pending local File
  const [imageBlobUrl, setImageBlobUrl]   = useState(null);   // blob preview URL
  const [imageServerUrl, setImageServerUrl] = useState(null); // final server URL
  const [wmNatural, setWmNatural]         = useState({ w: 1, h: 1 });
  const [dragOver, setDragOver]           = useState(false);
  const fileInputRef = useRef(null);

  // Settings
  const [position, setPosition]       = useState("Center");
  const [opacity, setOpacity]         = useState(0.8);
  const [sizePercent, setSizePercent] = useState(0.15);

  // Preview container sizes (measured via ResizeObserver)
  const portraitRef  = useRef(null);
  const landscapeRef = useRef(null);
  const [portraitSz,  setPortraitSz]  = useState({ w: 0, h: 0 });
  const [landscapeSz, setLandscapeSz] = useState({ w: 0, h: 0 });

  // ── Load existing watermark ──────────────────────────────────────────────
  useEffect(() => {
    getWatermark()
      .then((res) => {
        // Handle all possible response shapes:
        // { Result: [{...}] }  |  { Result: {...} }  |  [{...}]  |  {...}
        const raw = res.data?.Result ?? res.data ?? null;
        let wm = null;
        if (Array.isArray(raw)) {
          wm = raw[0] ?? null;
        } else if (raw && typeof raw === "object" && (raw.Id || raw.WatermarkImageUrl)) {
          wm = raw;
        }

        if (wm) {
          setExistingWm(wm);
          if (wm.WatermarkImageUrl) setImageServerUrl(wm.WatermarkImageUrl);
          if (wm.Position)          setPosition(wm.Position);
          if (wm.Opacity != null)   setOpacity(Number(wm.Opacity));
          if (wm.SizePercent != null) setSizePercent(Number(wm.SizePercent));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // ── Get natural size of watermark image when URL changes ─────────────────
  const displayUrl = imageBlobUrl || imageServerUrl;
  useEffect(() => {
    if (!displayUrl) return;
    const img = new Image();
    img.onload = () => setWmNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = displayUrl;
  }, [displayUrl]);

  // ── Measure preview containers (callback refs so sizes are set immediately on mount) ─
  const measureContainers = useCallback(() => {
    if (portraitRef.current) {
      const w = portraitRef.current.offsetWidth;
      if (w > 0) setPortraitSz({ w, h: Math.round(w * (4 / 3)) });
    }
    if (landscapeRef.current) {
      const w = landscapeRef.current.offsetWidth;
      if (w > 0) setLandscapeSz({ w, h: Math.round(w * (3 / 4)) });
    }
  }, []);

  useEffect(() => {
    if (loading) return; // body not rendered yet
    // Measure immediately after body appears
    measureContainers();
    const ro = new ResizeObserver(measureContainers);
    if (portraitRef.current)  ro.observe(portraitRef.current);
    if (landscapeRef.current) ro.observe(landscapeRef.current);
    return () => ro.disconnect();
  }, [loading, measureContainers]);

  // Cleanup blob URL on unmount
  useEffect(() => () => { if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl); }, [imageBlobUrl]);

  // ── File select handler ──────────────────────────────────────────────────
  const handleFileSelect = useCallback((file) => {
    if (!file?.type.startsWith("image/")) { toast.warning("Please select an image file"); return; }
    if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
    setImageFile(file);
    setImageBlobUrl(URL.createObjectURL(file));
    setImageServerUrl(null);
  }, [imageBlobUrl]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    let finalUrl = imageServerUrl;

    if (imageFile && !imageServerUrl) {
      setUploading(true);
      try {
        const res = await uploadWatermarkImage(imageFile, countryCode);
        // BulkCompressed returns: { status, result: [{ original, compressed }] }
        const arr = res.data?.result ?? res.data?.Result ?? null;
        if (Array.isArray(arr) && arr.length > 0) {
          finalUrl = arr[0]?.original ?? arr[0]?.compressed ?? null;
        }

        if (!finalUrl || !String(finalUrl).startsWith("http")) {
          throw new Error("Invalid URL returned from server");
        }
        setImageServerUrl(finalUrl);
        setImageFile(null);
      } catch (err) {
        toast.error(err.message || "Failed to upload watermark image");
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    if (!finalUrl) { toast.warning("Please upload a watermark image first"); return; }

    const payload = {
      WatermarkImageUrl: finalUrl,
      Position: position,
      Opacity: opacity,
      SizePercent: Math.round(sizePercent * 100) / 100,
    };

    setSaving(true);
    try {
      if (existingWm?.Id) {
        await updateWatermark(existingWm.Id, payload);
        toast.success("Watermark updated!");
      } else {
        await createWatermark(payload);
        toast.success("Watermark saved!");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to save watermark");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!existingWm?.Id) return;
    if (!window.confirm("Remove watermark settings? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await deleteWatermark(existingWm.Id);
      toast.success("Watermark removed");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Failed to remove watermark");
    } finally {
      setDeleting(false);
    }
  };

  // ── Watermark size calculation for preview ───────────────────────────────
  const calcWmSize = (containerW, containerH) => {
    if (!containerW || !containerH) return { w: 0, h: 0 };
    const shortest = Math.min(containerW, containerH);
    const wmW = shortest * sizePercent;
    const ar = wmNatural.w / (wmNatural.h || 1);
    return { w: wmW, h: wmW / ar };
  };

  const portWm  = calcWmSize(portraitSz.w,  portraitSz.h);
  const landWm  = calcWmSize(landscapeSz.w, landscapeSz.h);

  const isBusy = saving || uploading || deleting;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <span style={S.headerIcon}>🔏</span>
            <div>
              <h3 style={S.headerTitle}>Promoter Watermark</h3>
              <p style={S.headerSub}>Applied to all watermarked uploads</p>
            </div>
          </div>
          <button style={S.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Body ── */}
        {loading ? (
          <div style={S.loadingBox}>Loading settings…</div>
        ) : (
          <div style={S.body}>

            {/* ── Left: Settings ── */}
            <div style={S.leftCol}>

              {/* Image upload */}
              <div style={S.section}>
                <label style={S.sectionLabel}>Watermark Image</label>
                <div
                  style={{
                    ...S.dropZone,
                    ...(dragOver ? S.dropZoneActive : {}),
                    ...(displayUrl ? S.dropZoneWithImage : {}),
                  }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {displayUrl ? (
                    <>
                      <img src={displayUrl} alt="Watermark preview" style={S.dropPreviewImg} />
                      <div style={S.dropChangeOverlay}>
                        <span style={S.dropChangeText}>Click to change</span>
                      </div>
                    </>
                  ) : (
                    <div style={S.dropHintBox}>
                      <div style={S.dropHintIcon}>🖼️</div>
                      <div style={S.dropHintTitle}>Drop image here or click to upload</div>
                      <div style={S.dropHintSub}>PNG with transparency recommended</div>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                />
                {imageFile && (
                  <div style={S.pendingNote}>⚠ New image will be uploaded on save</div>
                )}
              </div>

              {/* Position picker */}
              <div style={S.section}>
                <label style={S.sectionLabel}>Position</label>
                <div style={S.posGrid}>
                  {GRID_MAP.flat().map((val, i) => {
                    if (!val) return <div key={i} style={S.posEmpty} />;
                    const pos = POSITIONS.find((p) => p.value === val);
                    const active = position === val;
                    return (
                      <button
                        key={val}
                        title={pos.title}
                        style={{ ...S.posCell, ...(active ? S.posCellActive : {}) }}
                        onClick={() => setPosition(val)}
                      >
                        {pos.label}
                      </button>
                    );
                  })}
                </div>
                <div style={S.posCurrentLabel}>
                  {POSITIONS.find((p) => p.value === position)?.title}
                </div>
              </div>

              {/* Opacity slider */}
              <div style={S.section}>
                <div style={S.sliderHeader}>
                  <label style={S.sectionLabel}>Opacity</label>
                  <span style={S.sliderBadge}>{Math.round(opacity * 100)}%</span>
                </div>
                <input
                  type="range"
                  min={0.1} max={1.0} step={0.05}
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                  style={S.sliderInput}
                />
                <div style={S.sliderTicks}><span>10%</span><span>100%</span></div>
              </div>

              {/* Size slider */}
              <div style={S.section}>
                <div style={S.sliderHeader}>
                  <label style={S.sectionLabel}>Size</label>
                  <span style={S.sliderBadge}>{Math.round(sizePercent * 100)}%</span>
                </div>
                <input
                  type="range"
                  min={0.05} max={0.5} step={0.01}
                  value={sizePercent}
                  onChange={(e) => setSizePercent(Number(e.target.value))}
                  style={S.sliderInput}
                />
                <div style={S.sliderTicks}><span>5%</span><span>50%</span></div>
              </div>

            </div>

            {/* ── Right: Live Preview ── */}
            <div style={S.rightCol}>
              <label style={S.sectionLabel}>Live Preview</label>

              <div style={S.previewRow}>

                {/* Portrait */}
                <div style={S.previewCard}>
                  <div style={S.previewCardLabel}>Portrait</div>
                  <div
                    ref={portraitRef}
                    style={{ ...S.previewCanvas, paddingTop: "133.33%" }}
                  >
                    <div style={S.previewBg} />
                    {/* Photo-like gradient overlay */}
                    <div style={{ ...S.previewGrad, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }} />
                    {displayUrl && portraitSz.w > 0 && (
                      <img
                        src={displayUrl}
                        alt="wm"
                        draggable={false}
                        style={{
                          ...S.wmOverlay,
                          width:   portWm.w,
                          height:  portWm.h,
                          opacity: opacity,
                          ...calcPositionStyle(position, portraitSz.w, portraitSz.h),
                        }}
                      />
                    )}
                    {!displayUrl && (
                      <div style={S.previewNoImage}>No image</div>
                    )}
                  </div>
                </div>

                {/* Landscape */}
                <div style={S.previewCard}>
                  <div style={S.previewCardLabel}>Landscape</div>
                  <div
                    ref={landscapeRef}
                    style={{ ...S.previewCanvas, paddingTop: "75%" }}
                  >
                    <div style={S.previewBg} />
                    <div style={{ ...S.previewGrad, background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }} />
                    {displayUrl && landscapeSz.w > 0 && (
                      <img
                        src={displayUrl}
                        alt="wm"
                        draggable={false}
                        style={{
                          ...S.wmOverlay,
                          width:   landWm.w,
                          height:  landWm.h,
                          opacity: opacity,
                          ...calcPositionStyle(position, landscapeSz.w, landscapeSz.h),
                        }}
                      />
                    )}
                    {!displayUrl && (
                      <div style={S.previewNoImage}>No image</div>
                    )}
                  </div>
                </div>

              </div>

              {/* Info note */}
              <div style={S.infoBox}>
                <strong>ℹ️ How it works:</strong> The watermark is applied server-side to every
                uploaded photo. Size is relative to the shortest side of each photo, and 3% padding
                is added for all corner positions.
              </div>
            </div>

          </div>
        )}

        {/* ── Footer ── */}
        <div style={S.footer}>
          <div style={S.footerLeft}>
            {existingWm?.Id && (
              <button
                style={{ ...S.deleteBtn, opacity: deleting ? 0.6 : 1 }}
                onClick={handleDelete}
                disabled={isBusy}
              >
                {deleting ? "Removing…" : "🗑 Remove Watermark"}
              </button>
            )}
          </div>
          <div style={S.footerRight}>
            <button style={S.cancelBtn} onClick={onClose} disabled={isBusy}>Cancel</button>
            <button
              style={{ ...S.saveBtn, opacity: isBusy ? 0.65 : 1, cursor: isBusy ? "not-allowed" : "pointer" }}
              onClick={handleSave}
              disabled={isBusy}
            >
              {uploading ? "Uploading image…" : saving ? "Saving…" : existingWm?.Id ? "Update Watermark" : "Save Watermark"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const S = {
  // Modal chrome
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
    padding: "16px",
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 860,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },

  // Header
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 24px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fafafa",
    flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  headerIcon: { fontSize: 28 },
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  headerSub: { margin: "2px 0 0", fontSize: 12, color: "#888" },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 18, color: "#999",
    cursor: "pointer", padding: "4px 8px",
    borderRadius: 6,
    lineHeight: 1,
    transition: "color 0.15s",
  },

  loadingBox: {
    padding: "60px 24px",
    textAlign: "center",
    color: "#888",
    fontSize: 14,
  },

  // Body layout
  body: {
    display: "flex",
    gap: 0,
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  },

  // Left column (settings)
  leftCol: {
    flex: "0 0 300px",
    padding: "20px 24px",
    borderRight: "1px solid #f0f0f0",
    overflowY: "auto",
  },

  // Right column (preview)
  rightCol: {
    flex: 1,
    padding: "20px 24px",
    overflowY: "auto",
    background: "#fafafa",
  },

  // Section
  section: { marginBottom: 24 },
  sectionLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "#6c63ff",
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 8,
  },

  // Drop zone
  dropZone: {
    border: "2px dashed #ddd",
    borderRadius: 10,
    background: "#fafafa",
    minHeight: 110,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s",
    overflow: "hidden",
    position: "relative",
  },
  dropZoneActive: {
    borderColor: "#6c63ff",
    background: "#f0eeff",
  },
  dropZoneWithImage: {
    border: "2px solid #e0e0e0",
    background: "#f5f5f5",
  },
  dropPreviewImg: {
    maxHeight: 110, maxWidth: "100%",
    objectFit: "contain",
    display: "block",
    margin: "auto",
    padding: 8,
  },
  dropChangeOverlay: {
    position: "absolute", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.15s",
    // CSS :hover not available inline — we apply this via the parent hover
  },
  dropChangeText: {
    color: "#fff", fontSize: 13, fontWeight: 600,
  },
  dropHintBox: { textAlign: "center", padding: "12px 16px" },
  dropHintIcon: { fontSize: 28, marginBottom: 6 },
  dropHintTitle: { fontSize: 13, color: "#555", fontWeight: 500, marginBottom: 4 },
  dropHintSub: { fontSize: 11, color: "#aaa" },
  pendingNote: {
    marginTop: 6,
    fontSize: 11,
    color: "#d97706",
    background: "#fef3c7",
    padding: "4px 10px",
    borderRadius: 6,
  },

  // Position grid
  posGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 40px)",
    gridTemplateRows: "repeat(3, 40px)",
    gap: 6,
    width: "fit-content",
    marginBottom: 8,
  },
  posCell: {
    width: 40, height: 40,
    border: "2px solid #e0e0e0",
    borderRadius: 8,
    background: "#f5f5f5",
    cursor: "pointer",
    fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
    color: "#666",
  },
  posCellActive: {
    border: "2px solid #6c63ff",
    background: "#ede9ff",
    color: "#6c63ff",
    boxShadow: "0 0 0 3px rgba(108,99,255,0.15)",
  },
  posEmpty: {
    width: 40, height: 40,
  },
  posCurrentLabel: {
    fontSize: 12, color: "#6c63ff", fontWeight: 600,
  },

  // Sliders
  sliderHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  sliderBadge: {
    fontSize: 12, fontWeight: 700, color: "#fff",
    background: "#6c63ff",
    padding: "2px 8px", borderRadius: 20,
  },
  sliderInput: {
    width: "100%",
    accentColor: "#6c63ff",
    cursor: "pointer",
    height: 4,
  },
  sliderTicks: {
    display: "flex", justifyContent: "space-between",
    fontSize: 10, color: "#bbb",
    marginTop: 4,
  },

  // Preview
  previewRow: {
    display: "flex", gap: 16,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  previewCard: {
    flex: 1,
    minWidth: 120,
  },
  previewCardLabel: {
    fontSize: 11, fontWeight: 600, color: "#888",
    textAlign: "center",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  previewCanvas: {
    position: "relative",
    width: "100%",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  },
  previewBg: {
    position: "absolute", inset: 0,
    background: "#ccc",
  },
  previewGrad: {
    position: "absolute", inset: 0,
  },
  wmOverlay: {
    position: "absolute",
    objectFit: "contain",
    pointerEvents: "none",
    maxWidth: "none",
  },
  previewNoImage: {
    position: "absolute", inset: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "rgba(255,255,255,0.5)",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.5,
  },

  infoBox: {
    background: "#f0f4ff",
    border: "1px solid #d0dbff",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 12,
    color: "#445",
    lineHeight: 1.6,
    marginTop: 4,
  },

  // Footer
  footer: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 24px",
    borderTop: "1px solid #f0f0f0",
    background: "#fafafa",
    flexShrink: 0,
    flexWrap: "wrap",
    gap: 10,
  },
  footerLeft: { display: "flex", alignItems: "center" },
  footerRight: { display: "flex", gap: 10 },

  deleteBtn: {
    background: "none",
    border: "1.5px solid #fca5a5",
    borderRadius: 8,
    color: "#ef4444",
    fontSize: 13,
    fontWeight: 600,
    padding: "7px 14px",
    cursor: "pointer",
  },
  cancelBtn: {
    background: "#f5f5f5",
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    color: "#555",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 18px",
    cursor: "pointer",
  },
  saveBtn: {
    background: "linear-gradient(135deg, #6c63ff 0%, #a78bfa 100%)",
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    padding: "8px 20px",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(108,99,255,0.35)",
  },
};

export default WatermarkSettingsModal;
